/**
 * Google Drive connector — conversation-side API.
 *
 * The picker ("Add from Google Drive") and the artifact panel's "Save to
 * Google Drive" both talk to the cloud-storage module through this file. The
 * OAuth connection itself is owned by Settings → Cloud Storage; nothing here
 * ever sees a token.
 */

import api from '@/services/api';
import type { DriveEdit, DriveFileRef } from '@/types/models';

const API_BASE = '/cloud-storage/google_drive';

export interface DriveFolder {
  folder_id: string;
  name: string;
}

export interface SavedDriveFile {
  file_id: string;
  name: string;
  mime_type?: string | null;
  web_view_link: string | null;
}

/**
 * Pull a Drive file id out of anything a user might paste: a Docs / Sheets /
 * Slides URL, a `/file/d/<id>/view` link, an `open?id=` link, or a bare id.
 * Returns null when the text is clearly a search query, not a link.
 */
export function parseDriveFileId(text: string): string | null {
  const raw = text.trim();
  if (!raw) return null;
  const pathMatch = raw.match(/\/(?:d|folders)\/([A-Za-z0-9_-]{20,})/);
  if (pathMatch) return pathMatch[1];
  const queryMatch = raw.match(/[?&]id=([A-Za-z0-9_-]{20,})/);
  if (queryMatch) return queryMatch[1];
  if (/^[A-Za-z0-9_-]{25,}$/.test(raw) && !/\s/.test(raw)) return raw;
  return null;
}

export const driveService = {
  async listRecent(): Promise<DriveFileRef[]> {
    const response = await api.get(`${API_BASE}/recent`);
    return response.data.files;
  },

  async search(query: string): Promise<DriveFileRef[]> {
    // In the body, never the URL: the platform's request log keeps every URL.
    const response = await api.post(`${API_BASE}/search`, { q: query });
    return response.data.files;
  },

  async getFile(fileId: string): Promise<DriveFileRef> {
    const response = await api.get(`${API_BASE}/files/${encodeURIComponent(fileId)}`);
    return response.data.file;
  },

  async listFolders(parentId?: string): Promise<DriveFolder[]> {
    const params = parentId ? { parent_id: parentId } : {};
    const response = await api.get(`${API_BASE}/folders`, { params });
    return response.data.folders;
  },

  async listFiles(folderId?: string): Promise<DriveFileRef[]> {
    const params = folderId ? { folder_id: folderId } : {};
    const response = await api.get(`${API_BASE}/files`, { params });
    return response.data.files;
  },

  async listEdits(conversationId: string): Promise<DriveEdit[]> {
    const response = await api.get(`/conversations/${conversationId}/drive-edits`);
    return response.data.edits || [];
  },

  async revertEdit(conversationId: string, editId: string): Promise<{ reverted: string[]; edits: DriveEdit[] }> {
    const response = await api.post(`/conversations/${conversationId}/drive-edits/${editId}/revert`);
    return response.data;
  },

  async saveArtifact(payload: {
    artifact_file_id: string;
    version?: number;
    folder_id?: string;
    file_name?: string;
    convert_to_native: boolean;
  }): Promise<SavedDriveFile> {
    const response = await api.post(`${API_BASE}/save-artifact`, payload);
    return response.data.file;
  },
};

/** Which native Google app a Drive mime type belongs to (for labels/icons). */
export type DriveKind = 'doc' | 'sheet' | 'slides' | 'pdf' | 'image' | 'folder' | 'word' | 'excel' | 'powerpoint' | 'text' | 'numbers' | 'pages' | 'keynote' | 'other';

export function driveKind(mime: string | undefined, name: string): DriveKind {
  const m = mime || '';
  if (m === 'application/vnd.google-apps.document') return 'doc';
  if (m === 'application/vnd.google-apps.spreadsheet') return 'sheet';
  if (m === 'application/vnd.google-apps.presentation') return 'slides';
  if (m === 'application/vnd.google-apps.folder') return 'folder';
  if (m === 'application/pdf') return 'pdf';
  if (m.startsWith('image/')) return 'image';
  const ext = (name.split('.').pop() || '').toLowerCase();
  if (['doc', 'docx'].includes(ext) || m.includes('wordprocessingml')) return 'word';
  if (['xls', 'xlsx', 'xlsm', 'csv'].includes(ext) || m.includes('spreadsheetml')) return 'excel';
  if (['ppt', 'pptx'].includes(ext) || m.includes('presentationml')) return 'powerpoint';
  if (['txt', 'md', 'json', 'xml', 'log'].includes(ext) || m.startsWith('text/')) return 'text';
  if (ext === 'numbers') return 'numbers';
  if (ext === 'pages') return 'pages';
  if (ext === 'key') return 'keynote';
  return 'other';
}

/** Artifact formats that Drive can open as a live Google Doc / Sheet / Slides. */
export function canOpenAsGoogleFormat(format: string | undefined): 'doc' | 'sheet' | 'slides' | null {
  switch (format) {
    case 'docx':
    case 'markdown':
    case 'html':
      return 'doc';
    // A .numbers file stays a .numbers file in Drive: Google has no importer for it.
    case 'xlsx':
      return 'sheet';
    case 'pptx':
      return 'slides';
    default:
      return null;
  }
}

/* ── Is this person's Google Drive actually connected? ────────────────────
 *
 * The composer and the artifact panel only change shape for someone who HAS
 * a Drive connection: without one they keep the plain paperclip and the plain
 * action row they have always had, and the place to connect stays where it
 * already is — Settings → Cloud Storage. One request per page load, shared by
 * every conversation and every panel, so opening a chat costs nothing.
 */
let connectedPromise: Promise<boolean> | null = null;

export function driveConnected(): Promise<boolean> {
  if (!connectedPromise) {
    connectedPromise = api
      .get('/cloud-storage/connections')
      .then((res) => (res.data?.connections || []).some(
        (c: { provider?: string; connection_status?: string }) =>
          c.provider === 'google_drive' && c.connection_status === 'active',
      ))
      .catch(() => false);
  }
  return connectedPromise;
}

/** Forget the cached answer — call after connecting or disconnecting. */
export function forgetDriveConnected(): void {
  connectedPromise = null;
}
